import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  PlusCircle,
  Trash2,
  Calendar as CalendarIcon,
  AlertTriangle,
  Sparkles,
} from "lucide-react";

const CADENCES = [
  { value: "daily", label: "Daily" },
  { value: "mon_wed_fri", label: "Mon / Wed / Fri" },
  { value: "weekdays", label: "Weekdays (Mon–Fri)" },
  { value: "custom", label: "Custom days" },
];

const VISIBILITY = [
  { value: "unlisted", label: "Unlisted (recommended)" },
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
];

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function SeriesNew() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [contentType, setContentType] = useState("music");
  const [length, setLength] = useState("short");
  const [catalog, setCatalog] = useState({ types: [] });
  const [picked, setPicked] = useState([]);
  const [customTitle, setCustomTitle] = useState("");
  const [customKey, setCustomKey] = useState("");
  const [cadence, setCadence] = useState("mon_wed_fri");
  const [days, setDays] = useState(["mon", "wed", "fri"]);
  const [timeLocal, setTimeLocal] = useState("09:00");
  const [tz, setTz] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
  );
  const [visibility, setVisibility] = useState("unlisted");
  const [autoPublish, setAutoPublish] = useState(true);
  const [estimate, setEstimate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    api.get("/videos/catalog").then((r) => setCatalog(r.data));
  }, []);

  useEffect(() => {
    if (picked.length > 0) {
      api
        .get(`/series/_estimate/cost?topic_count=${picked.length}`)
        .then((r) => setEstimate(r.data))
        .catch(() => setEstimate(null));
    } else {
      setEstimate(null);
    }
  }, [picked.length]);

  const activeType = useMemo(
    () => catalog.types.find((t) => t.key === contentType),
    [catalog.types, contentType],
  );

  const togglePick = (topic) => {
    setPicked((prev) => {
      const existing = prev.findIndex((p) => p.topic_key === topic.id);
      if (existing >= 0) {
        const next = [...prev];
        next.splice(existing, 1);
        return next;
      }
      if (prev.length >= 10) return prev;
      return [...prev, { title: topic.label, topic_key: topic.id }];
    });
  };

  const addCustom = () => {
    if (!customTitle.trim() || !customKey.trim()) return;
    if (picked.length >= 10) return;
    setPicked((prev) => [
      ...prev,
      { title: customTitle.trim(), topic_key: customKey.trim() },
    ]);
    setCustomTitle("");
    setCustomKey("");
  };

  const removePick = (i) =>
    setPicked((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const r = await api.post("/series", {
        name: name.trim(),
        description: description.trim() || null,
        content_type: contentType,
        length,
        topics: picked,
        schedule: {
          enabled: true,
          cadence,
          days: cadence === "custom" ? days : [],
          time_local: timeLocal,
          timezone: tz,
          publish_visibility: visibility,
        },
        autopilot: { enabled: true, auto_publish: autoPublish },
      });
      nav(`/series/${r.data.id}`);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || e.message);
      setSubmitting(false);
    }
  };

  const canNext = (s) => {
    if (s === 1) return name.trim().length > 0;
    if (s === 2) return contentType && length;
    if (s === 3) return picked.length >= 1;
    if (s === 4) return cadence && timeLocal;
    return true;
  };

  return (
    <div data-testid="series-new" className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-stone-500">
        <span>Step {step} of 5</span>
        <div className="h-1 flex-1 overflow-hidden rounded-full bg-stone-100">
          <div
            className="h-full rounded-full bg-orange-500 transition-all"
            style={{ width: `${(step / 5) * 100}%` }}
          />
        </div>
      </div>

      {step === 1 ? (
        <Card className="border-orange-200 bg-white/80">
          <CardHeader>
            <CardTitle>Name your series</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-stone-700">
                Series name
              </label>
              <Input
                data-testid="series-name"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 120))}
                placeholder="Classic Nursery Rhymes — Week 1"
                className="border-orange-200"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-stone-700">
                Description (optional)
              </label>
              <textarea
                data-testid="series-description"
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 400))}
                rows={3}
                className="w-full rounded-md border border-orange-200 bg-white px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === 2 ? (
        <Card className="border-orange-200 bg-white/80">
          <CardHeader>
            <CardTitle>Pick a content type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              {catalog.types.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`series-type-${t.key}`}
                  onClick={() => setContentType(t.key)}
                  className={`relative rounded-xl border-2 p-4 text-left transition ${
                    contentType === t.key
                      ? "border-orange-500 bg-orange-50 ring-2 ring-orange-200"
                      : "border-stone-200 hover:border-orange-300"
                  }`}
                >
                  {t.badge ? (
                    <Badge className="absolute -top-2 right-3 bg-amber-500 text-white">
                      {t.badge}
                    </Badge>
                  ) : null}
                  <div className="text-sm font-bold text-stone-800">
                    {t.label}
                  </div>
                  <p className="mt-1 text-xs text-stone-500">{t.description}</p>
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              {["short", "long"].map((L) => (
                <button
                  key={L}
                  type="button"
                  data-testid={`series-length-${L}`}
                  onClick={() => setLength(L)}
                  className={`flex-1 rounded-lg border p-3 text-sm transition ${
                    length === L
                      ? "border-orange-500 bg-orange-50 font-semibold"
                      : "border-stone-200 hover:border-orange-300"
                  }`}
                >
                  {L === "short" ? "Short (~1-2 min)" : "Long (~3-4 min)"}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === 3 ? (
        <Card className="border-orange-200 bg-white/80">
          <CardHeader>
            <CardTitle>
              Pick your topics{" "}
              <span className="text-sm font-normal text-stone-500">
                ({picked.length}/10)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
              {(activeType?.topics || []).map((t) => {
                const on = picked.some((p) => p.topic_key === t.id);
                return (
                  <button
                    type="button"
                    key={t.id}
                    data-testid={`topic-${t.id}`}
                    onClick={() => togglePick(t)}
                    className={`rounded-lg border p-2 text-left text-sm transition ${
                      on
                        ? "border-orange-500 bg-orange-50 font-semibold text-orange-800"
                        : "border-stone-200 hover:border-orange-300"
                    }`}
                  >
                    {on ? "✓ " : ""}{t.label}
                  </button>
                );
              })}
            </div>

            <div className="rounded-lg border border-dashed border-orange-300 bg-orange-50/40 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-stone-700">
                Or add a custom topic
              </div>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <Input
                  data-testid="custom-topic-title"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="Title (e.g. Three Little Pigs)"
                  className="flex-1 border-orange-200"
                />
                <Input
                  data-testid="custom-topic-key"
                  value={customKey}
                  onChange={(e) =>
                    setCustomKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))
                  }
                  placeholder="topic_key"
                  className="flex-1 border-orange-200"
                />
                <Button
                  type="button"
                  onClick={addCustom}
                  disabled={!customTitle.trim() || !customKey.trim()}
                  className="bg-orange-500 text-white hover:bg-orange-600"
                  data-testid="custom-topic-add"
                >
                  <PlusCircle className="mr-1 h-4 w-4" /> Add
                </Button>
              </div>
            </div>

            {picked.length > 0 ? (
              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone-700">
                  Selected
                </div>
                <ol
                  data-testid="picked-list"
                  className="list-decimal space-y-1 pl-5 text-sm text-stone-800"
                >
                  {picked.map((p, i) => (
                    <li key={i} className="flex items-center justify-between">
                      <span>
                        {p.title}{" "}
                        <code className="text-xs text-stone-500">
                          ({p.topic_key})
                        </code>
                      </span>
                      <button
                        type="button"
                        onClick={() => removePick(i)}
                        data-testid={`unpick-${i}`}
                        className="text-stone-400 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {step === 4 ? (
        <Card className="border-orange-200 bg-white/80">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" /> Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-stone-700">
                Cadence
              </label>
              <div className="grid gap-2 sm:grid-cols-4">
                {CADENCES.map((c) => (
                  <button
                    type="button"
                    key={c.value}
                    data-testid={`cadence-${c.value}`}
                    onClick={() => setCadence(c.value)}
                    className={`rounded-lg border p-2 text-sm transition ${
                      cadence === c.value
                        ? "border-orange-500 bg-orange-50 font-semibold"
                        : "border-stone-200 hover:border-orange-300"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              {cadence === "custom" ? (
                <div className="mt-3 flex flex-wrap gap-1">
                  {DAYS.map((d) => (
                    <button
                      type="button"
                      key={d}
                      onClick={() =>
                        setDays((prev) =>
                          prev.includes(d)
                            ? prev.filter((x) => x !== d)
                            : [...prev, d],
                        )
                      }
                      className={`rounded-full border px-3 py-1 text-xs uppercase ${
                        days.includes(d)
                          ? "border-orange-500 bg-orange-100 font-semibold"
                          : "border-stone-200"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-stone-700">
                  Publish time (local)
                </label>
                <Input
                  data-testid="schedule-time"
                  type="time"
                  value={timeLocal}
                  onChange={(e) => setTimeLocal(e.target.value)}
                  className="border-orange-200"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-stone-700">
                  Timezone
                </label>
                <Input
                  data-testid="schedule-tz"
                  value={tz}
                  onChange={(e) => setTz(e.target.value)}
                  className="border-orange-200"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-stone-700">
                Visibility on YouTube
              </label>
              <div className="grid gap-2 sm:grid-cols-3">
                {VISIBILITY.map((v) => (
                  <button
                    type="button"
                    key={v.value}
                    onClick={() => setVisibility(v.value)}
                    data-testid={`visibility-${v.value}`}
                    className={`rounded-lg border p-2 text-sm transition ${
                      visibility === v.value
                        ? "border-orange-500 bg-orange-50 font-semibold"
                        : "border-stone-200 hover:border-orange-300"
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-orange-100 bg-orange-50/40 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-stone-800">
                  Auto-publish
                </div>
                <div className="text-xs text-stone-600">
                  When OFF, ready videos wait for you to click Publish.
                </div>
              </div>
              <Switch
                data-testid="auto-publish-toggle"
                checked={autoPublish}
                onCheckedChange={setAutoPublish}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === 5 ? (
        <Card className="border-orange-200 bg-white/80">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-orange-500" /> Review &amp; create
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-stone-700">
            <div>
              <strong>Name:</strong> {name}
            </div>
            <div>
              <strong>Type:</strong> {contentType} · <strong>Length:</strong>{" "}
              {length}
            </div>
            <div>
              <strong>Topics:</strong> {picked.length}{" "}
              <span className="text-stone-500">
                ({picked.map((p) => p.title).join(", ")})
              </span>
            </div>
            <div>
              <strong>Schedule:</strong> {cadence} at {timeLocal} ({tz})
              {cadence === "custom" ? ` · ${days.join(", ")}` : ""}
            </div>
            <div>
              <strong>Visibility:</strong> {visibility} ·{" "}
              <strong>Auto-publish:</strong> {autoPublish ? "yes" : "no"}
            </div>
            {estimate ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                Estimated total cost:{" "}
                <strong data-testid="cost-estimate">
                  ${estimate.estimated_cost_usd}
                </strong>{" "}
                ({picked.length} videos × ~$1.40/each).
                {estimate.estimated_cost_usd > 25 ? (
                  <div className="mt-1 flex items-center gap-1 text-xs text-amber-800">
                    <AlertTriangle className="h-3 w-3" /> Heads up — large series.
                  </div>
                ) : null}
              </div>
            ) : null}
            {errorMsg ? (
              <div
                data-testid="series-error"
                className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {errorMsg}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <div className="flex justify-between">
        <Button
          variant="outline"
          disabled={step === 1 || submitting}
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          data-testid="series-back"
        >
          <ChevronLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        {step < 5 ? (
          <Button
            disabled={!canNext(step)}
            onClick={() => setStep((s) => s + 1)}
            data-testid="series-next"
            className="bg-orange-500 text-white hover:bg-orange-600"
          >
            Next <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <Button
            disabled={submitting || picked.length === 0 || !name.trim()}
            onClick={submit}
            data-testid="series-create"
            className="bg-emerald-600 text-white hover:bg-emerald-700"
          >
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Create series
          </Button>
        )}
      </div>
    </div>
  );
}
